import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { PrayerRow, AsrFilterOptions } from "./excelExport";

const MONTH_NAMES_NO: Record<number, string> = {
  1: "Januar",
  2: "Februar",
  3: "Mars",
  4: "April",
  5: "Mai",
  6: "Juni",
  7: "Juli",
  8: "August",
  9: "September",
  10: "Oktober",
  11: "November",
  12: "Desember",
};

const WEEKDAY_NO: Record<number, string> = {
  0: "Man",
  1: "Tir",
  2: "Ons",
  3: "Tor",
  4: "Fre",
  5: "Lør",
  6: "Søn",
};

function parseDate(dateStr: string): Date {
  const [dd, mm, yyyy] = dateStr.split("-");
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
}

function getLastSunday(year: number, month: number): Date {
  const lastDay = new Date(year, month, 0);
  let d = lastDay;
  while (d.getDay() !== 0) {
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  }
  return d;
}

/**
 * Genererer Excel-filen først, deretter konverterer til PDF
 * Hver måned får sin egen side i PDF-en
 */
export async function generatePDF(
  rows: PrayerRow[],
  cityName: string,
  year: number,
  asrFilter?: AsrFilterOptions,
) {
  if (!rows.length) return;

  // Generer Excel-filen først (i minnet)
  const workbook = new ExcelJS.Workbook();

  // Sorter og grupper etter måned
  const grouped: Record<string, { date: Date; row: PrayerRow }[]> = {};
  for (const r of rows) {
    const d = parseDate(r.date);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push({ date: d, row: r });
  }

  // Sørg for sortering per måned
  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  const lastSundayMarch = getLastSunday(year, 3);
  const lastSundayOctober = getLastSunday(year, 10);

  // Styles (samme som i excelExport)
  const headerFont = { bold: true, size: 14, color: { argb: "FF000000" } };
  const headerFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFCCFFCC" },
  };
  const monthHeaderFont = {
    bold: true,
    size: 18,
    color: { argb: "FF000000" },
  };
  const monthHeaderFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFFFCC00" },
  };
  const redFont = { color: { argb: "FFFF0000" } };
  const redFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFFFC7CE" },
  };
  const stripeWhite = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFFFFFFF" },
  };
  const stripeGray = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFD9D9D9" },
  };
  const dataFont = { size: 13 };

  // Finn ekstremperiode
  let extremeStarted = false;
  let extremeEnded = false;
  let prevIshaMissing = false;
  let extremeStartMonth: number | null = null;
  let extremeEndMonth: number | null = null;
  const extremeMonths = new Set<number>();

  for (let month = 1; month <= 12; month++) {
    const key = `${year}-${month}`;
    const monthRows = grouped[key];
    if (!monthRows || monthRows.length === 0) continue;

    let hasExtremeThisMonth = false;
    for (const { row } of monthRows) {
      const isha = row.isha;
      if (isha === "--:--") {
        hasExtremeThisMonth = true;
      }
      if (!extremeStarted && isha === "--:--") {
        extremeStarted = true;
        prevIshaMissing = true;
        if (extremeStartMonth === null) extremeStartMonth = month;
      } else if (extremeStarted && !extremeEnded) {
        if (isha === "--:--") {
          prevIshaMissing = true;
        } else {
          if (prevIshaMissing && extremeEndMonth === null) {
            extremeEnded = true;
            extremeEndMonth = month;
          }
          prevIshaMissing = false;
        }
      }
    }
    if (hasExtremeThisMonth) {
      extremeMonths.add(month);
    }
  }

  // Opprett PDF
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Reset for rad-styling
  extremeStarted = false;
  extremeEnded = false;
  prevIshaMissing = false;
  let applyNextRowRedFajr = false;

  // Generer en side per måned basert på Excel-strukturen
  for (let month = 1; month <= 12; month++) {
    const key = `${year}-${month}`;
    const monthRows = grouped[key];
    if (!monthRows || monthRows.length === 0) continue;

    // Legg til ny side hvis ikke første måned
    if (month > 1) {
      doc.addPage();
    }

    // Tittel med gul bakgrunn (matcher Excel)
    const pageWidth = doc.internal.pageSize.getWidth();
    const title = `${cityName} - ${MONTH_NAMES_NO[month]} ${year}`;

    // Tegn gul bakgrunn for tittel
    doc.setFillColor(255, 204, 0); // Gul/orange som Excel FFFFCC00
    doc.rect(15, 8, pageWidth - 30, 12, "F"); // x, y, width, height, "F" = filled

    // Skriv tittel-tekst (midtstilt)
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, 17); // Midtstilt

    // Forbered tabell-data
    const tableData: (string | number)[][] = [];
    const rowStyles: any[] = [];

    let monthDataRowIndex = 0;
    for (const { date, row } of monthRows) {
      const day = date.getDate();
      const weekday =
        WEEKDAY_NO[date.getDay() === 0 ? 6 : date.getDay() - 1];

      const isExtremeStartRow =
        !extremeStarted && row.isha === "--:--";
      const isExtremeEndRow =
        extremeStarted && !extremeEnded && prevIshaMissing && row.isha !== "--:--";

      if (isExtremeStartRow) {
        extremeStarted = true;
        prevIshaMissing = true;
        applyNextRowRedFajr = true;
      } else if (isExtremeEndRow) {
        extremeEnded = true;
        applyNextRowRedFajr = true;
      } else if (extremeStarted && row.isha === "--:--") {
        prevIshaMissing = true;
      } else {
        prevIshaMissing = false;
      }

      const shouldHighlightFajr = applyNextRowRedFajr;
      applyNextRowRedFajr = false;

      // DST-markering
      const isDSTDay =
        (date.getFullYear() === lastSundayMarch.getFullYear() &&
          date.getMonth() === lastSundayMarch.getMonth() &&
          date.getDate() === lastSundayMarch.getDate()) ||
        (date.getFullYear() === lastSundayOctober.getFullYear() &&
          date.getMonth() === lastSundayOctober.getMonth() &&
          date.getDate() === lastSundayOctober.getDate());

      // Bygg rad-data basert på Asr-filter
      const rowData: (string | number)[] = [
        day,
        weekday,
        row.fajr,
        row.fajr_endtime,
        row.duhr,
      ];
      if (!asrFilter?.onlyHanafi) rowData.push(row.asr_1x_shadow);
      if (!asrFilter?.onlyShafi) rowData.push(row.asr_2x_shadow);
      rowData.push(row.maghrib, row.isha);

      tableData.push(rowData);

      // Bestem rad-styling
      const isExtremeRow = isExtremeStartRow || isExtremeEndRow;
      const isStripe = monthDataRowIndex % 2 === 0;

      // Lagre om neste rad skal ha rød Fajr
      const shouldHighlightNextFajr = isExtremeStartRow || isExtremeEndRow;

      rowStyles.push({
        fillColor: isExtremeRow ? [255, 199, 206] : isStripe ? [255, 255, 255] : [217, 217, 217],
        textColor: isDSTDay ? [255, 0, 0] : [0, 0, 0],
        fontStyle: isDSTDay ? "bold" : "normal",
        fontSize: isDSTDay ? 11 : 10,
        shouldHighlightFajr: shouldHighlightNextFajr, // For neste rad
      });

      monthDataRowIndex++;
    }

    // Definer kolonneoverskrifter basert på Asr-filter
    const headers: string[][] = [["Dato", "Ukedag", "Fajr", "Soloppgang", "Dhuhr"]];
    if (!asrFilter?.onlyHanafi) headers[0].push(asrFilter?.onlyShafi ? "Asr" : "Asr\nShafi'i");
    if (!asrFilter?.onlyShafi) headers[0].push(asrFilter?.onlyHanafi ? "Asr" : "Asr\nHanafi");
    headers[0].push("Maghrib", "Isha");

    // Generer tabell med riktig styling (bedre formatering som Python-scriptet)
    autoTable(doc, {
      head: headers,
      body: tableData,
      startY: 30, // Litt lavere for bedre plassering
      styles: {
        fontSize: 9,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
        overflow: "linebreak",
        cellWidth: "wrap",
        lineColor: [128, 128, 128], // Grå border
        lineWidth: 0.5,
      },
      headStyles: {
        fillColor: [204, 255, 204], // Lys grønn
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10,
        halign: "center",
        valign: "middle",
      },
      // Spesial styling for header og data-celler
      didParseCell: (data: any) => {
        // Header-rad styling
        if (data.section === 'head') {
          // Første to kolonner (Dato og Ukedag) skal ha gul bakgrunn (matcher Excel FFFFCC00)
          if (data.column.index === 0 || data.column.index === 1) {
            data.cell.styles.fillColor = [255, 204, 0]; // Gul/orange som Excel
          }
          // Resten av header har allerede grønn fra headStyles
        }

        // Data-rader
        if (data.section === 'body') {
          const rowIndex = data.row.index;
          if (rowStyles[rowIndex]) {
            const style = rowStyles[rowIndex];
            data.cell.styles.fillColor = style.fillColor;
            data.cell.styles.textColor = style.textColor;
            data.cell.styles.fontStyle = style.fontStyle;
            data.cell.styles.fontSize = style.fontSize;

            // Fajr-kolonnen (index 2) skal ha rød tekst hvis forrige rad var ekstrem start/slutt
            if (data.column.index === 2 && rowIndex > 0 && rowStyles[rowIndex - 1]?.shouldHighlightFajr) {
              data.cell.styles.textColor = [255, 0, 0];
            }
          }
        }
      },
      columnStyles: (() => {
        // Beregn tilgjengelig bredde
        const availableWidth = pageWidth - 30; // 15mm margin på hver side

        // Definer relative breddeforhold for hver kolonne
        // Disse er basisverdier som vil skaleres proporsjonalt
        const baseWidths: { [key: string]: number } = {
          "0": 1.0,   // Dato
          "1": 1.5,   // Ukedag
          "2": 1.3,   // Fajr
          "3": 2.0,   // Soloppgang
          "4": 1.3,   // Dhuhr
        };

        let colIdx = 5;
        const asrCols: number[] = [];
        if (!asrFilter?.onlyHanafi) {
          baseWidths[String(colIdx)] = 1.5; // Asr Shafi'i
          asrCols.push(colIdx++);
        }
        if (!asrFilter?.onlyShafi) {
          baseWidths[String(colIdx)] = 1.5; // Asr Hanafi
          asrCols.push(colIdx++);
        }
        baseWidths[String(colIdx++)] = 1.5; // Maghrib
        baseWidths[String(colIdx++)] = 1.3; // Isha

        // Beregn total relativ bredde
        const totalRelativeWidth = Object.values(baseWidths).reduce((sum, w) => sum + w, 0);

        // Skaler til faktisk bredde
        const scaleFactor = availableWidth / totalRelativeWidth;

        const styles: { [key: string]: { cellWidth: number; halign: "center"; valign: "middle" } } = {};
        for (const [key, relWidth] of Object.entries(baseWidths)) {
          styles[key] = {
            cellWidth: relWidth * scaleFactor,
            halign: "center",
            valign: "middle"
          };
        }

        return styles;
      })(),
      margin: { top: 30, left: 15, right: 15 }, // Sentrert med like marger
      showHead: "everyPage",
      showFoot: "never",
      useCss: false,
    });

    // Fotnote
    const lastY = (doc as any).lastAutoTable?.finalY || 250;
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(102, 102, 102);
    const footnote = `www.mawaqeet.no ${year} - bønnetider i Norge - generert av iBønn`;
    const footnoteWidth = doc.getTextWidth(footnote);
    doc.text(footnote, pageWidth - footnoteWidth - 10, lastY + 10);

    // Sommertid-tekst
    if (month === 3) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(255, 0, 0);
      doc.text("Klokken stilles én time frem", 10, lastY + 20);
    }

    if (month === 10) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(255, 0, 0);
      doc.text("Klokken stilles én time tilbake (sommertidslutt)", 10, lastY + 20);
    }

    // Ekstremperiode start/slutt tekst - venstrejustert, over forklaringsteksten
    if (extremeStartMonth !== null && month === extremeStartMonth) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 0, 0);
      const extremeStartText = "Ekstremperiode start - Fajr: Nisf-ul-layl";
      doc.text(extremeStartText, 10, lastY + 10);
    }

    if (extremeEndMonth !== null && month === extremeEndMonth) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 0, 0);
      const extremeEndText = "Ekstremperiode slutt - Fajr: 18 grader";
      doc.text(extremeEndText, 10, lastY + 10);
    }

    // Ekstremperiode-forklaring - bare for måneder i ekstremperioden (under fotnote/ekstrem-tekst)
    if (extremeMonths.has(month)) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const explanation = [
        "Under ekstremperioden har Isha fellestid med Maghrib fram til Fajr. Man deler da perioden mellom solnedgang og Fajr i",
        "to, hvor den første delen gis til Maghrib og den andre til Isha. Hvis Isha er veldig sen og det er vanskelig å vente kan man",
        "be Isha med et passende mellomrom etter Maghrib.",
      ];
      let yPos = lastY + 20; // Under fotnote/ekstrem-tekst
      for (const line of explanation) {
        const lines = doc.splitTextToSize(line, pageWidth - 20);
        doc.text(lines, 10, yPos);
        yPos += lines.length * 5;
      }
    }
  }

  // Last ned PDF
  doc.save(`${cityName} ${year}.pdf`);
}
